// Libraries
import React, { CSSProperties, ReactNode, useState, useRef, useEffect } from 'react';
import { ForwardedRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { isNotNil } from 'ramda';

// MUI
import {
    DataGridPro,
    GridCallbackDetails,
    GridCellModesModel,
    GridColumnHeaderParams,
    GridComparatorFn,
    GridEventListener,
    GridFilterModel,
    GridInputRowSelectionModel,
    GridPaginationModel,
    GridPinnedColumnFields,
    GridRenderCellParams,
    GridRowOrderChangeParams,
    GridRowParams,
    GridRowSelectionModel,
    GridRowsProp,
    GridSortModel,
    MuiEvent,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { SxProps, Theme, styled, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { LicenseInfo } from '@mui/x-license';

// Core
import { ResourceTextApplication } from '../../core/resources';

// Feature - Data Grid
import ADataGridButtonGroup from './ADataGridButtonGroup';
import { CustomToolbar } from './customSlots/CustomToolbar';
import { CustomNoResultsOverlay } from './customSlots/CustomNoResultsOverlay';
import { CustomNoRowsOverlay } from './customSlots/CustomNoRowsOverlay';
import { CustomLoadingOverlay } from './customSlots/CustomLoadingOverlay';
import gridUtils from './gridUtils';

interface ADataGridAction {
    (params: GridRenderCellParams): ReactNode;
}

export type ADataGridActions = ADataGridAction[];

export interface IDataGridValueOptions {
    value: any;
    label: string;
}

export interface AGridColumn {
    field: string;
    filterable?: boolean;
    flex?: number;
    headerName?: string;
    /** Row data is also accessible via renderCell by params.row.
     * @param params
     * @returns
     */
    renderCell?: (params: GridRenderCellParams) => ReactNode;
    renderHeader?: (params: GridColumnHeaderParams) => ReactNode;
    sortable?: boolean;
    sortComparator?: GridComparatorFn;
    type?: 'string' | 'number' | 'date' | 'dateTime' | 'boolean' | 'singleSelect';
    /** Row has the row data, params.value has the field data and through params.api you can access the grid col api.
     * More info on: https://mui.com/x/api/data-grid/grid-col-def/ && https://mui.com/x/migration/migration-data-grid-v6/
     * @param params
     * @param row
     * @returns
     */
    valueGetter?: (params: any, row: any) => any;
    valueOptions?: Array<string | number | IDataGridValueOptions>;
    width?: number;
    description?: string;
    align?: 'left' | 'right' | 'center';
    headerAlign?: 'left' | 'right' | 'center';
    editable?: boolean;
    valueFormatter?: (value: any, row: any, column: any) => any;
}

export type AGridColumns = AGridColumn[];

export interface ADataGridProps {
    actions?: ADataGridActions;
    className?: string;
    columns: AGridColumns;
    enableFiltering?: boolean;
    enablePagination?: boolean;
    enableRowNumbers?: boolean;
    rowReordering?: boolean;
    disableSelectionOnClick?: boolean;
    pinnedColumns?: GridPinnedColumnFields; 
    getRowClassName?: (params: GridRowParams, details?: any) => string;
    isRowSelectable?: (params: GridRowParams, details?: any) => boolean;
    loading?: boolean;
    onRowClick?: (
        params: GridRowParams,
        event: MuiEvent<React.SyntheticEvent>,
        details?: any,
    ) => void;
    onSelectionModelChange?: (model: GridRowSelectionModel) => void;
    onRowOrderChange?: (params: GridRowOrderChangeParams) => Promise<void>;
    onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
    onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
    rows: GridRowsProp;
    selectionModel?: GridInputRowSelectionModel | undefined;
    stickyHeader?: boolean;
    apiRef?: React.MutableRefObject<GridApiPro> | undefined;
    getDetailedPanelContent?: (params: GridRowParams) => React.ReactNode;
}

LicenseInfo.setLicenseKey(
    'a953607dd1c5289e1ce502c517cc500fTz0xMjQ1MjgsRT0xODA0NjM2Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI=',
);

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    border: 'none !important',
    zIndex: 0,
    '&.an-data-grid-sticky-header > div:first-of-type': {
        backgroundColor: '#fff',
        height: '36px',
        position: 'sticky',
        top: '0px',
        zIndex: 1,
    },
    '&.an-data-grid-sticky-header .MuiDataGrid-main > .MuiDataGrid-columnsContainer': {
        backgroundColor: '#fff',
        position: 'sticky',
        top: '36px',
        zIndex: 1,
    },
    position: 'relative',
    '.MuiDataGrid-main': {
        color: 'rgba(0, 0, 0, 0.87)',
    },
    '.MuiDataGrid-cell': {
        alignContent: 'center',
    },
    '.MuiDataGrid-hiddenSortBtns': {
        'div[aria-sort]': {
            '.MuiDataGrid-iconButtonContainer': {
                width: 'inherit',
                display: 'inherit',
            },
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            '.MuiDataGrid-iconButtonContainer': {
                width: '0',
                display: 'none',
            },
            '&:hover': {
                '.MuiDataGrid-iconButtonContainer': {
                    width: 'inherit',
                    display: 'inherit',
                },
            },
        },
    },
}));

export default forwardRef(function ADataGrid(props: ADataGridProps, ref: ForwardedRef<any>) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral']);

    const apiRef = useGridApiRef();
    const selectionModel: GridInputRowSelectionModel | undefined = props.selectionModel;

    const {
        actions,
        enableFiltering,
        enablePagination,
        enableRowNumbers,
        rowReordering,
        disableSelectionOnClick,
        onSelectionModelChange,
        onRowOrderChange,
        onFilterModelChange,
        onSortModelChange,
        loading,
        stickyHeader,
        getRowClassName,
        isRowSelectable,
        getDetailedPanelContent,
        pinnedColumns,
        ...nativeProps
    } = props;

    const enableRowSelection = !!onSelectionModelChange;
    const stickyHeaderClass = stickyHeader ? 'an-data-grid-sticky-header' : '';
    const columns2 = [...props.columns];
    const rowNumbersCss: CSSProperties = { color: '#888', fontSize: '12px' };

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 20,
        page: 0,
    });

    type TopScrollToolbarProps = {
        topScrollRef: React.RefObject<HTMLDivElement>;
        topScrollInnerRef: React.RefObject<HTMLDivElement>;
    };

    function TopScrollToolbar(props: TopScrollToolbarProps) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <CustomToolbar />

                <Box
                    ref={props.topScrollRef}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        backgroundColor: 'transparent',
                        border: 'none',
                        height: 14,
                        lineHeight: 0,
                        mt: 0.5,
                        mb: 0.5,
                        scrollbarWidth: 'thin',
                        scrollbarGutter: 'stable',

                        // IMPORTANT: prevent hidden scrollbar from stealing hover/click
                        visibility: 'hidden',
                        pointerEvents: 'none',
                    }}
                >
                    <Box ref={props.topScrollInnerRef} sx={{ height: 1 }} />
                </Box>
            </Box>
        );
    }

    if (actions && actions.length) {
        columns2.unshift({
            field: 'Actions',
            filterable: false,
            renderCell: (params: any) => {
                return <ADataGridButtonGroup>{actions.map((a) => a(params))}</ADataGridButtonGroup>;
            },
            renderHeader: () => <div />,
            sortable: false,
            width: actions.length * 38 + 28,
        });
    }

    if (enableRowNumbers) {
        columns2.unshift({
            field: 'RowNumber',
            filterable: false,
            headerName: '#',
            sortable: false,
            renderCell: (params) => (
                <span style={rowNumbersCss} data-robot-id={`data-grid-row-id-${params}`}>
                    {params.api.getAllRowIds().indexOf(params.id) + 1}
                </span>
            ),
            renderHeader: () => <span style={rowNumbersCss}>#</span>,
            width: 50,
        });
    }

    const handleFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
        if (isNotNil(props.apiRef)) {
            if (props.apiRef.current.state.pagination.paginationModel.page > 0)
                props.apiRef.current.setPage(0);
        } else {
            if (apiRef?.current?.state.pagination.paginationModel.page > 0)
                apiRef.current.setPage(0);
        }

        if (onFilterModelChange) {
            onFilterModelChange(model, details);
        }
    };

    // Conditional styles applied here. Please, you are welcome to move this to StyledDataGrid if you know how to use styled outside of component with component props http://jira.mtech.fi/browse/AN-2061
    const enableConditionalOverlayStyling = (): SxProps<Theme> | undefined => {
        return props.rows.length === 0
            ? {
                  '.MuiDataGrid-overlayWrapper': {
                      height: '100px',
                  },
                  '.MuiDataGrid-virtualScroller': {
                      overflowX: 'hidden',
                  },
              }
            : {};
    };

    const getApiRef = () => {
        if (isNotNil(props.apiRef)) return props.apiRef;
        else if (isNotNil(apiRef)) return apiRef;
    };

    const topScrollRef = useRef<HTMLDivElement>(null);
    const topScrollInnerRef = useRef<HTMLDivElement>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);

    // Sync scrollbars
    useEffect(() => {
        const topScroll = topScrollRef.current;
        const topInner = topScrollInnerRef.current;
        const gridRoot = gridContainerRef.current;

        if (!topScroll || !topInner || !gridRoot) return;

        let gridScroller: HTMLElement | null = null;
        const cleanupFns: Array<() => void> = [];
        let raf = 0;

        // Never let the (hidden) scrollbar steal hover/click events from menus/poppers
        const setTopScrollbarVisible = (visible: boolean) => {
            // Reserve space but remove from hit-testing when hidden
            topScroll.style.visibility = visible ? 'visible' : 'hidden';
            topScroll.style.pointerEvents = visible ? 'auto' : 'none';
        };

        const measureAndSync = () => {
            if (!gridScroller) return;

            // Set the inner spacer width to match the scrollable content
            const scrollWidth = gridScroller.scrollWidth;
            topInner.style.width = `${scrollWidth}px`;

            // Show/hide WITHOUT layout reflow (prevents jumps and hover-bugs)
            const needsHScroll = scrollWidth > gridScroller.clientWidth + 1;
            setTopScrollbarVisible(needsHScroll);

            // Match the grid’s vertical scrollbar gutter so the thumb feels identical
            const vScrollbarWidth = gridScroller.offsetWidth - gridScroller.clientWidth;
            topScroll.style.paddingRight = vScrollbarWidth > 0 ? `${vScrollbarWidth}px` : '0px';
        };

        const scheduleMeasure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measureAndSync);
        };

        const init = () => {
            gridScroller = gridRoot.querySelector(
                '.MuiDataGrid-virtualScroller',
            ) as HTMLElement | null;
            if (!gridScroller) return;

            const onTopScroll = () => {
                if (!gridScroller) return;
                if (gridScroller.scrollLeft !== topScroll.scrollLeft) {
                    gridScroller.scrollLeft = topScroll.scrollLeft;
                }
            };

            const onGridScroll = () => {
                if (!gridScroller) return;
                if (topScroll.scrollLeft !== gridScroller.scrollLeft) {
                    topScroll.scrollLeft = gridScroller.scrollLeft;
                }
            };

            topScroll.addEventListener('scroll', onTopScroll, { passive: true });
            gridScroller.addEventListener('scroll', onGridScroll, { passive: true });

            cleanupFns.push(() => topScroll.removeEventListener('scroll', onTopScroll));
            cleanupFns.push(() => gridScroller?.removeEventListener('scroll', onGridScroll));

            // Initial state: hidden + non-interactive until we measure
            setTopScrollbarVisible(false);

            // Measure once and then observe layout changes
            scheduleMeasure();

            const ro = new ResizeObserver(scheduleMeasure);
            ro.observe(gridRoot);
            ro.observe(gridScroller);

            cleanupFns.push(() => ro.disconnect());

            // Also handle font load / first paint issues
            const t = window.setTimeout(scheduleMeasure, 0);
            cleanupFns.push(() => window.clearTimeout(t));
        };

        init();

        return () => {
            cancelAnimationFrame(raf);
            cleanupFns.forEach((fn) => fn());
        };
    }, [props.rows, props.columns, paginationModel.pageSize, stickyHeader]);

    return (
        <Box sx={{ position: 'relative' }} ref={gridContainerRef}>
            <Box
                ref={topScrollRef}
                sx={{
                    position: 'sticky',
                    top: stickyHeader ? 36 : 0, // matches your toolbar height
                    zIndex: 0,

                    overflowX: 'auto',
                    overflowY: 'hidden',
                    height: 14,
                    lineHeight: 0,
                    mb: 0.5,

                    backgroundColor: 'transparent',
                    border: 'none',
                    scrollbarWidth: 'thin',
                    scrollbarGutter: 'stable',
                }}
            >
                <Box ref={topScrollInnerRef} sx={{ height: 1 }} />
            </Box>

            <StyledDataGrid
                autoHeight
                apiRef={getApiRef()}
                slots={{
                    toolbar: () => (
                        <TopScrollToolbar
                            topScrollRef={topScrollRef}
                            topScrollInnerRef={topScrollInnerRef}
                        />
                    ),
                    noResultsOverlay: CustomNoResultsOverlay,
                    noRowsOverlay: CustomNoRowsOverlay,
                    loadingOverlay: CustomLoadingOverlay,
                    detailPanelExpandIcon: () => (
                        <KeyboardArrowDownIcon style={{ height: '18px', width: '18px' }} />
                    ),
                    detailPanelCollapseIcon: () => (
                        <KeyboardArrowUpIcon style={{ height: '18px', width: '18px' }} />
                    ),
                }}
                className={clsx(props.className, stickyHeaderClass)}
                columns={columns2}
                ref={ref}
                rowSelectionModel={selectionModel}
                onFilterModelChange={handleFilterModelChange}
                rows={props.rows}
                disableRowSelectionOnClick={disableSelectionOnClick ?? false}
                checkboxSelection={enableRowSelection}
                disableColumnFilter={!enableFiltering}
                pagination={enablePagination ?? false}
                pageSizeOptions={enablePagination ? [paginationModel.pageSize] : undefined}
                paginationMode={'client'}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnMenu={true}
                disableColumnReorder={true}
                disableColumnResize={true}
                disableColumnSelector={true}
                disableDensitySelector={true}
                getRowClassName={getRowClassName}
                isRowSelectable={isRowSelectable}
                hideFooter={!enablePagination && !enableRowSelection}
                hideFooterPagination={!enablePagination}
                hideFooterRowCount={enablePagination === true ? false : true}
                hideFooterSelectedRowCount={!enableRowSelection}
                onRowSelectionModelChange={onSelectionModelChange}
                localeText={gridUtils.getGridLocaleTxt(t)}
                loading={loading}
                onRowClick={props.onRowClick}
                rowHeight={33}
                sx={enableConditionalOverlayStyling()}
                rowReordering={rowReordering}
                onRowOrderChange={onRowOrderChange}
                onSortModelChange={onSortModelChange}
                initialState={{ pinnedColumns: pinnedColumns }}
                // makes row collapsible
                getDetailPanelContent={getDetailedPanelContent}
                getDetailPanelHeight={() => 'auto'}
            />
        </Box>
    );
});
